import React, { useState, useContext, useEffect } from 'react';
import { getUserLogin, getCurrentUser } from '../services/user-api';
import { AuthContext } from '../reusable-components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import { NavigateToPageRoute } from '../utils/common-functions';
import { showToastMessage } from '../utils/toast-functions';
import { TextInput } from '../reusable-components/TextInput';
import { useSearchParams } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';

export const LoginPage = () => {
  const authContext = useContext(AuthContext);
  const { setToken, setIsAdmin } = authContext;
  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const [queryInProgress, setQueryInProgress] = useState<boolean>(false);
  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showMessage = searchParams.get('showMessage');
  const messageType = searchParams.get('messageType');

  const handleLogin = async (userName: string, password: string) => {
    setQueryInProgress(true);
    setLoginError('');
    const result = await getUserLogin(userName, password);

    if (result.success) {
      setToken(result.token);
      setLoginError('');

      // Fetch the current user information to check admin status
      const currentUser = await getCurrentUser(result.token);
      if (currentUser) {
        setIsAdmin(currentUser.admin || false);
      } else {
        setIsAdmin(false);
      }

      setQueryInProgress(false);
      NavigateToPageRoute('/rating-jobs', setCurrentRoute, navigate);
    } else {
      setToken('');
      setIsAdmin(false);
      setLoginError(result.errorMessage ?? 'Unknown error while logging in');
      setQueryInProgress(false);
    }
  };

  useEffect(() => {
    if (showMessage) {
      showToastMessage(showMessage, messageType);
    }
  }, [showMessage, messageType]);

  return (
    <div className="md:container md:mx-auto px-32">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Slide}
      />
      <h1 className="text-2xl font-bold my-4">Login</h1>

      <TextInput
        label="Username:"
        value={userName}
        placeholder="Enter user name"
        onChange={(e) => setUserName(e.target.value)}
        required={true}
        errorMessage="User name is required"
      />

      <TextInput
        label="Password:"
        value={password}
        placeholder="Enter password"
        onChange={(e) => setPassword(e.target.value)}
        onEnterKeyPressed={() => password && handleLogin(userName, password)}
        fieldType="password"
        required={true}
        errorMessage="Password is required"
      />

      <div className="max-w-xs py-4 flex justify-center">
        <button
          data-testid="login-button"
          className="w-full btn btn-primary w-40"
          onClick={() => handleLogin(userName, password)}
          disabled={!userName || !password || queryInProgress}
        >
          Login
        </button>
      </div>
      <div className="max-w-xs flex justify-center">
        <div className="text-error">{loginError}</div>
        {queryInProgress && (
          <div className="loading loading-spinner loading-lg w-10"></div>
        )}
      </div>
    </div>
  );
};
